import {
  Box,
  Flex,
  FormControl,
  SimpleGrid,
  Stack,
  Text,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import { taskAction } from 'actions';
import ModelSelector from 'components/ModelSelector';
import Textarea from 'components/Textarea';
import { useFormik } from 'formik';
import { SET_TIMEOUT_STREAMING_ANSWER_Task } from 'helpers/constant';
import { TASK_FILE_STATUS, TASK_STATUS } from 'helpers/enum';
import { getUserInfoStorage, setLastSelectedModel } from 'helpers/lib';
import { ArrowUp } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'services/model/model.type';
import { SummaryService } from 'services/summary/summary.service';

import Message from './Message';

interface ChatProps {
  onAddNew: () => void;
}

const Chat = ({ onAddNew }: ChatProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 30em)');
  const [isTablet] = useMediaQuery('(max-width: 48em)');

  const chatRef = useRef<HTMLDivElement>(null);
  const cancelEditRef = useRef<HTMLButtonElement>(null);
  // Holds a suggestion clicked on the no-task greeting; prefilled into the composer once the
  // task is created (Summary needs files before submit, so we prefill rather than auto-send). #77529r2-7
  const pendingPromptRef = useRef<string | null>(null);
  const userInfo = getUserInfoStorage();

  const dispatch = useDispatch();
  const { status, current, isLimitReached, processingStreamingChunkStatusTask } = useSelector(
    (state) => state.task
  );

  const models = useSelector((state: any) => state.model?.models || []);

  const handleModelChange = (model: Model | null) => {
    if (model) {
      setLastSelectedModel(model.id);
      dispatch(
        taskAction.updateCurrentTask({
          task_id: current?.task_id,
          type: current?.type,
          selectedModelId: model.id,
        })
      );
    }
  };

  const isExistFileProcessing = current.selectedFiles.some(
    (file) => file.task_file_status === TASK_FILE_STATUS.UPLOADED
  );
  const isGenerateAnswer = status === TASK_STATUS.GENERATE_ANSWER;
  const isDisableQuestionBox =
    !current?.task_id ||
    isGenerateAnswer ||
    isLimitReached ||
    Boolean(current.answerChunks.part_of_answer);
  let timer: ReturnType<typeof setTimeout> | any;

  const { values, handleChange, handleSubmit, resetForm, setFieldValue } = useFormik({
    initialValues: {
      question: '',
    },
    onSubmit: (values) => {
      if (current.selectedFiles.length === 0) {
        toast({
          title: `Please add files before submit`,
          status: 'warning',
          position: 'bottom-right',
          isClosable: true,
        });
        return;
      }
      if (isExistFileProcessing) {
        toast({
          title: `Please wait file finish processing before submit`,
          status: 'warning',
          position: 'bottom-right',
          isClosable: true,
        });
        return;
      }
      if (values.question && values.question?.trim()) {
        if (current?.task_id) {
          // Get selected model info
          const selectedModel = models.find((m: Model) => m.id === current.selectedModelId);
          dispatch(
            taskAction.addNewQuestion({
              ...values,
              created_at: new Date().toISOString(),
              model: selectedModel
                ? {
                    id: selectedModel.id,
                    name: selectedModel.name,
                  }
                : undefined,
            })
          );
          dispatch(taskAction.updateStatus(TASK_STATUS.GENERATE_ANSWER));
          dispatch(taskAction.updateProcessTaskId(current.task_id));
          dispatch(taskAction.updateProcessingStreamingChunkStatusTask(true));
          dispatch(taskAction.setTimeOutForFinalAnswerTask(true));
        }
        resetForm();
      }
      cancelEditRef?.current?.click();
    },
  });

  useEffect(() => {
    if (current.setTimeOutFinalAnswerTask && current.task_id) {
      timer = setTimeout(async () => {
        const response = await SummaryService.getLatestMessageTask({
          id: current.task_id!,
        });

        if (response) {
          dispatch(
            taskAction.updateNewAnswer({
              id: response.id,
              answer: response.answer,
              requested_model: response.requested_model,
              timestamp: response.updated_at,
              question: response.question,
            })
          );
          dispatch(taskAction.clearStreamingAnswerChunksTask());
          // Reset streaming flags so the fallback-timeout effect re-arms for the next message (H-1).
          dispatch(taskAction.setTimeOutForFinalAnswerTask(false));
          dispatch(taskAction.updateProcessingStreamingChunkStatusTask(false));
        } else {
          dispatch(taskAction.clearStreamingAnswerChunksTask());
          dispatch(taskAction.setTimeOutForFinalAnswerTask(false));
          dispatch(taskAction.updateProcessingStreamingChunkStatusTask(false));
        }
      }, SET_TIMEOUT_STREAMING_ANSWER_Task);
    }

    return () => clearTimeout(timer);
  }, [current.setTimeOutFinalAnswerTask, current.task_id, dispatch]);

  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
  }, []);

  const isDisableSubmit =
    !values.question || isGenerateAnswer || isLimitReached || processingStreamingChunkStatusTask;

  const onChangeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleChange(e);
  };

  // Suggestion cards (mockup): prefill composer + submit via existing formik flow (no new wiring).
  const suggestionCards = [
    { titleKey: 'summary.cardSummariseTitle', descKey: 'summary.cardSummariseDesc' },
    { titleKey: 'summary.cardCompareTitle', descKey: 'summary.cardCompareDesc' },
    { titleKey: 'summary.cardDatesTitle', descKey: 'summary.cardDatesDesc' },
    { titleKey: 'summary.cardRisksTitle', descKey: 'summary.cardRisksDesc' },
  ];

  // Clicking a suggestion FILLS the composer for review/edit — it does NOT auto-send.
  // (No active task yet → create one, then the effect below prefills the staged prompt.)
  const onClickSuggestion = async (prompt: string) => {
    if (!current?.task_id) {
      pendingPromptRef.current = prompt;
      onAddNew();
      return;
    }
    if (isDisableQuestionBox) return;
    await setFieldValue('question', prompt);
  };

  // When a task gets created after a greeting-suggestion click, prefill the staged prompt
  // (user adds files, then sends). #77529r2-7
  useEffect(() => {
    if (current?.task_id && pendingPromptRef.current) {
      const prompt = pendingPromptRef.current;
      pendingPromptRef.current = null;
      setFieldValue('question', prompt);
    }
  }, [current?.task_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedFilesCount = current?.selectedFiles?.length ?? 0;

  useEffect(() => {
    const scrollToBottom = () => {
      const chatElement = document.getElementById('message-container');
      if (chatElement) {
        chatElement.scrollTo({
          top: chatElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    };

    scrollToBottom();

    return () => {};
  }, [current, status]);

  return (
    /* DS output pane — surface card, ds_sm shadow (design-guidelines.md §4) */
    <Flex
      direction='column'
      flex={1}
      overflow='hidden'
      w='100%'
      h='100%'
      bg='bg.canvas'
      borderWidth={{ lg: '1px' }}
      borderColor={{ lg: 'border.default' }}
      borderRadius={{ lg: 'lg' }}
      shadow={{ lg: 'ds_sm' }}
    >
      {!current?.task_id ? (
        <Flex
          align={{ base: 'flex-start', lg: 'center' }}
          justify='center'
          flex={1}
          overflowY='auto'
          px={4}
          py={6}
        >
          {/* No active task — heading + suggestion cards (clicking starts a task + prefills) + start CTA */}
          <Flex align='center' direction='column' w='100%' maxW='820px' textAlign='center'>
            <Text
              color='text.default'
              fontFamily='heading'
              fontSize='2xl'
              fontWeight={600}
              letterSpacing='-0.02em'
            >
              {t('summary.summariseHeading')}
            </Text>
            <Text mt={3} color='text.muted' fontSize='base'>
              {t('summary.description')}
            </Text>
            {/* Suggestion cards on the greeting (#77529r2-7) */}
            <SimpleGrid w='100%' mt={8} columns={{ base: 1, md: 2 }} spacing={4}>
              {suggestionCards.map((card) => (
                <Box
                  key={card.titleKey}
                  as='button'
                  px={5}
                  py={4}
                  textAlign='left'
                  bg='bg.surface'
                  borderWidth='1px'
                  borderColor='border.default'
                  borderRadius='lg'
                  _hover={{ borderColor: 'primary.solid', bg: 'primary.soft', shadow: 'ds_xs' }}
                  cursor='pointer'
                  transition='all .14s ease-out'
                  onClick={() => onClickSuggestion(t(card.titleKey))}
                  type='button'
                >
                  <Text color='text.default' fontSize='sm' fontWeight={600}>
                    {t(card.titleKey)}
                  </Text>
                  <Text mt={1} color='text.subtle' fontSize='xs'>
                    {t(card.descKey)}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
            <Box
              as='button'
              h='46px'
              mt={6}
              px={6}
              color='primary.on'
              fontWeight={600}
              bg='primary.solid'
              borderRadius='md'
              shadow='ds_xs'
              _hover={{ bg: 'primary.hover' }}
              _active={{ transform: 'translateY(1px)' }}
              transition='background .16s ease-out'
              onClick={onAddNew}
            >
              {t('summary.start')}
            </Box>
          </Flex>
        </Flex>
      ) : current?.data?.length === 0 ? (
        /* Active task, no messages yet — welcome + 2x2 suggestion cards (mockup) */
        <Flex
          align={{ base: 'flex-start', lg: 'center' }}
          justify='center'
          flex={1}
          overflowY='auto'
          px={4}
          py={6}
        >
          <Flex align='center' direction='column' w='100%' maxW='820px' textAlign='center'>
            <Text
              color='text.default'
              fontFamily='heading'
              fontSize='2xl'
              fontWeight={600}
              letterSpacing='-0.02em'
            >
              {t('summary.summariseHeading')}
            </Text>
            <Text mt={3} color='text.muted' fontSize='base'>
              {selectedFilesCount > 0
                ? t('summary.summariseSubtitleWithCount', { count: selectedFilesCount })
                : t('summary.summariseSubtitle')}
            </Text>
            <SimpleGrid w='100%' mt={8} columns={{ base: 1, md: 2 }} spacing={4}>
              {suggestionCards.map((card) => (
                <Box
                  key={card.titleKey}
                  as='button'
                  px={5}
                  py={4}
                  textAlign='left'
                  bg='bg.surface'
                  opacity={isDisableQuestionBox ? 0.5 : 1}
                  borderWidth='1px'
                  borderColor='border.default'
                  borderRadius='lg'
                  _hover={
                    isDisableQuestionBox
                      ? undefined
                      : { borderColor: 'primary.solid', bg: 'primary.soft', shadow: 'ds_xs' }
                  }
                  cursor={isDisableQuestionBox ? 'not-allowed' : 'pointer'}
                  transition='all .14s ease-out'
                  onClick={() => onClickSuggestion(t(card.titleKey))}
                  type='button'
                >
                  <Text color='text.default' fontSize='sm' fontWeight={600}>
                    {t(card.titleKey)}
                  </Text>
                  <Text mt={1} color='text.subtle' fontSize='xs'>
                    {t(card.descKey)}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Flex>
        </Flex>
      ) : (
        <Box
          ref={chatRef}
          sx={{
            '&::-webkit-scrollbar': {
              display: isMobile ? 'none' : 'block',
            },
          }}
          flex={'1 1 0'}
          alignSelf={'stretch'}
          overflowY='auto'
          h='100%'
          minH={0}
          id='message-container'
        >
          <Box maxW={{ base: '100%', lg: '820px' }} m='auto' px={{ base: 4, md: 6 }}>
            <Stack direction='column' pt={0} spacing={{ base: 6, lg: '2rem' }}>
              {current?.data?.length > 0 &&
                current.data.map((message, index) => (
                  <Message
                    isLast={index === current.data.length - 1}
                    key={index}
                    data={message}
                    status={status}
                    user={userInfo!}
                    cancelEditRef={cancelEditRef}
                  />
                ))}
            </Stack>
          </Box>
        </Box>
      )}

      {/* DS composer — surface card, ds_md shadow, focus ring (design-guidelines.md §4) */}
      <Box
        pos='relative'
        zIndex={1000}
        flexShrink={0}
        px={{ base: 4, md: 8 }}
        pt={3}
        pb={{ base: 10, md: 6 }}
      >
        <form onSubmit={handleSubmit}>
          <FormControl>
            <Flex
              direction='column'
              w='100%'
              maxW={{ lg: '820px' }}
              m='auto'
              px={3}
              pt={2}
              pb={2}
              bg='bg.surface'
              borderWidth='1px'
              borderColor='border.strong'
              borderRadius='xl'
              shadow='ds_md'
              _focusWithin={{
                borderColor: 'primary.solid',
                boxShadow: '0 0 0 3px var(--chakra-colors-primary-soft)',
              }}
              transition='border-color .16s ease-out, box-shadow .16s ease-out'
            >
              <Textarea
                name='question'
                variant='unstyled'
                placeholder={t('summary.summarisePlaceholder')}
                rows={1}
                minH='44px'
                h='44px'
                maxH={isTablet ? '120px' : '180px'}
                border='none'
                borderRadius={0}
                bg='transparent'
                boxShadow='none'
                color='text.default'
                resize='none'
                _placeholder={{ color: 'text.subtle' }}
                _hover={{ bg: 'transparent' }}
                _focus={{ boxShadow: 'none', border: 'none', bg: 'transparent' }}
                _focusVisible={{ boxShadow: 'none', border: 'none', bg: 'transparent' }}
                _disabled={{ bg: 'transparent', opacity: 0.6, cursor: 'not-allowed' }}
                px={2}
                py={2}
                fontSize='base'
                maxExpandHeight={isTablet ? 120 : 180}
                isDisabled={isDisableQuestionBox}
                value={values.question}
                onChange={onChangeMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }
                }}
              />
              <Flex align='center' gap={2} mt={1}>
                {current?.task_id && (
                  <ModelSelector
                    selectedModelId={current.selectedModelId}
                    onModelChange={handleModelChange}
                    isDisabled={isDisableQuestionBox}
                    menuPlacement='top'
                    width='100%'
                    maxWidth='200px'
                  />
                )}
                <Flex
                  as='button'
                  align='center'
                  justify='center'
                  flexShrink={0}
                  w='38px'
                  h='38px'
                  ml='auto'
                  color='primary.on'
                  bg='primary.solid'
                  opacity={isDisableSubmit ? 0.4 : 1}
                  borderRadius='full'
                  _hover={{ bg: 'primary.hover' }}
                  cursor={isDisableSubmit ? 'not-allowed' : 'pointer'}
                  transition='background .16s ease-out, opacity .16s ease-out'
                  aria-label={t('common.inputPlaceholder')}
                  onClick={() => !isDisableSubmit && handleSubmit()}
                  type='button'
                >
                  <ArrowUp size={18} />
                </Flex>
              </Flex>
            </Flex>
          </FormControl>
        </form>
      </Box>
    </Flex>
  );
};

export default Chat;
